import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor, Box, Grid, Heading, ResponsiveContext, Tab, Tabs, Text, ThemeContext } from "grommet";
import { take } from "lodash";
import * as React from "react";
import Avatar from "react-avatar";
import { useParams } from "react-router-dom";
import { AliasCard } from "../aliases/AliasCard";
import CustomButton from "../components/CustomButton";
import RichTabTitle from "../components/RichTabTitle";
import SkeletonWrapper from "../components/SkeletonWrapper";
import { LeagueRow } from "../leagues/LeagueRow";
import { getTeamLeagues, LeagueViewModel } from "../leagues/LeaguesStore";
import { getTeamMatches, MatchViewModel } from "../matches/MatchesStore";
import { MatchRow } from "../matches/MatchRow";
import { SeasonRow } from "../seasons/SeasonRow";
import TeamMemberRole from "../teams/TeamMemberRole";
import { getTeam, getTeamMembers, TeamMembershipViewModel, TeamViewModel } from "../teams/TeamsStore";
import { TournamentRow } from "../tournaments/TournamentRow";
import { getTeamTournaments, TournamentViewModel } from "../tournaments/TournamentsStore";
import { LiveIndicator } from "./LiveIndicator";
import TeamHeader from "./TeamHeader";

const UXTeamHome = () => {
    let { id } = useParams<{ id: string }>();

    const [index, setIndex] = React.useState(0);
    const onActive = (nextIndex: number) => setIndex(nextIndex);

    const [members, setMembers] = React.useState([] as TeamMembershipViewModel[]);
    const [team, setTeam] = React.useState({} as TeamViewModel);
    const [upcomingMatches, setUpcomingMatches] = React.useState([] as MatchViewModel[]);
    const [pastMatches, setPastMatches] = React.useState([] as MatchViewModel[]);
    const [tournaments, setTournaments] = React.useState([] as TournamentViewModel[]);
    const [leagues, setLeagues] = React.useState([] as LeagueViewModel[]);

    const [isTeamPrivate, setIsTeamPrivate] = React.useState(false);
    const [isMembersPrivate, setIsMembersPrivate] = React.useState(false);

    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
        getTeamMembers(id).then((result) => {
            if (result.status == 200) {
                setMembers(result.data.result);
            } else {
                setIsMembersPrivate(true);
                setMembers([]);
            }
        });

        getTeam(id).then((result) => {
            if (result.status == 200) {
                setTeam(result.data.result);
            } else {
                setIsTeamPrivate(true);
                setTeam({} as TeamViewModel);
            }
            setIsLoading(false);
            console.log("interactive");
        });

        getTeamMatches(id).then((matches) => {
            setUpcomingMatches(matches.filter((m) => m.isUpcoming));
            setPastMatches(matches.filter((m) => !m.isUpcoming));
        });
        getTeamTournaments(id).then((tournaments) => setTournaments(tournaments));
        getTeamLeagues(id).then((leagues) => setLeagues(leagues));
    }, []);

    const size = React.useContext(ResponsiveContext);

    return (
        <Box align="center">
            <Box width={{ max: "xxlarge", min: size }} fill>
                <SkeletonWrapper loading={isLoading}>
                    <ThemeContext.Extend
                        value={{
                            global: {
                                colors: { brand: team.color, "accent-1": team.color },
                            },
                        }}
                    >
                        {isTeamPrivate && (
                            <Box align="center">
                                <Heading color={"background-front"}>{team.name} is private </Heading>
                            </Box>
                        )}
                        {!isTeamPrivate && (
                            <>
                                <TeamHeader name={team.name} logoUri={team.logoUri} splashUri={team.splashUri} countryCode={team.countryCode} rating={team.rating}></TeamHeader>

                                <Box direction="row" gap="small" justify="center">
                                    <CustomButton icon={<FontAwesomeIcon icon={["fad", "fire"]} />} label="Overview" onClick={() => onActive(0)}></CustomButton>
                                    <CustomButton icon={<FontAwesomeIcon icon={["fad", "users"]} />} label="Members" onClick={() => onActive(1)}></CustomButton>
                                    <CustomButton icon={<FontAwesomeIcon icon={["fad", "gamepad-modern"]} />} label="Matches" onClick={() => onActive(2)}></CustomButton>
                                    <CustomButton icon={<FontAwesomeIcon icon={["fad", "trophy"]} />} label="Tournaments" onClick={() => onActive(3)}></CustomButton>
                                    <CustomButton icon={<FontAwesomeIcon icon={["fad", "table-cells"]} />} label="Leagues" onClick={() => onActive(4)}></CustomButton>
                                </Box>
                                <Tabs activeIndex={index} onActive={onActive}>
                                    <Tab>
                                        <Box gap="medium">
                                            <Grid columns={["1/3", "2/3"]}>
                                                <Box height="small">
                                                    <Text weight={"bold"} style={{ textTransform: "uppercase" }}>
                                                        About Us
                                                    </Text>
                                                    <Box pad={"small"}>
                                                        <Text size="small">
                                                            <pre>{team.about}</pre>
                                                        </Text>
                                                    </Box>
                                                </Box>
                                                <Box align="end">
                                                    <Text weight={"bold"} style={{ textTransform: "uppercase" }}>
                                                        Team Members
                                                    </Text>
                                                    {isMembersPrivate && <Text color={"background-front"}>{team.name}s members are private</Text>}
                                                    {!isMembersPrivate && (
                                                        <Box direction="row" wrap gap="small" pad={"small"}>
                                                            {take(members, 6).map((member) => (
                                                                <AliasCard key={member.id} width={150} alias={member.alias}>
                                                                    <TeamMemberRole role={member.roleName}></TeamMemberRole>
                                                                </AliasCard>
                                                            ))}
                                                            {members.length > 6 && <Anchor onClick={() => onActive(1)}>See More</Anchor>}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Grid>
                                            <Text weight={"bold"} style={{ textTransform: "uppercase" }}>
                                                Upcoming Matches
                                            </Text>
                                            {upcomingMatches.map((match) => (
                                                <MatchRow key={match.id} match={match}></MatchRow>
                                            ))}
                                            <Text weight={"bold"} style={{ textTransform: "uppercase" }}>
                                                Latest Matches
                                            </Text>
                                            {pastMatches.map((match) => (
                                                <MatchRow key={match.id} match={match}></MatchRow>
                                            ))}
                                        </Box>
                                    </Tab>
                                    {/* 
                    <Tab title={<RichTabTitle icon={<FontAwesomeIcon icon={["fad", "list-timeline"]} />} label="Feed" />}>
                        <Box gap="medium">
                            <Box background={"background-back"} pad="medium" direction="row" gap="small">
                                <Box justify="center">
                                    <img src="/images/rgb.png" width="75px" alt="" />
                                </Box>
                                <Box>
                                    <Text>RGB Started Streaming</Text> <LiveIndicator></LiveIndicator>
                                    <Text size="small">
                                        Join us on our live streaming on{" "}
                                        <Anchor href="https://www.twitch.tv/directory" target="_blank">
                                            <FontAwesomeIcon icon={["fab", "twitch"]} /> Twitch <FontAwesomeIcon size="sm" icon={["fad", "arrow-up-right-from-square"]} />
                                        </Anchor>
                                    </Text>
                                </Box>
                            </Box>

                            <Box background={"background-back"} pad="medium" direction="row" gap="small">
                            
                                <Avatar name={"FantasyVegetable"} round={"18px"} size="75" src={`/images/fantasyvegetable.png`}></Avatar>
                                <Box justify="center">
                                    <Text size="small">
                                        {"FantasyVegetable"} earned 10 experience for joining a <span style={{ textDecoration: "underline" }}>league match</span>
                                    </Text>
                                </Box>
                            </Box>

                            <Box background={"background-back"} pad="medium" direction="row" gap="small">
                                <Box justify="center">
                                    <img src="/images/fox.png" width="75px" alt="" />
                                </Box>
                                <Box>
                                    <Text>Fox e-sports Stream</Text>
                                    <sup>
                                        <FontAwesomeIcon icon={["fad", "circle-small"]} /> stopped
                                    </sup>
                                    <Text size="small">
                                        You missed us!
                                        <br />
                                        You can still watch our stream on{" "}
                                        <Anchor href="https://www.twitch.tv/directory" target="_blank">
                                            <FontAwesomeIcon icon={["fab", "twitch"]} /> Twitch <FontAwesomeIcon size="sm" icon={["fad", "arrow-up-right-from-square"]} />
                                        </Anchor>
                                    </Text>
                                </Box>
                            </Box>

                            <Box background={"background-back"} pad="medium" direction="row" gap="small">
                                <Box justify="center">
                                    <img src="/images/knight.png" width="75px" alt="" />
                                </Box>
                                <Box>
                                    <Text>Knight e-sports wins!</Text>
                                    <sup>
                                        <Text size="xsmall" color="status-ok">
                                            3
                                        </Text>{" "}
                                        -{" "}
                                        <Text size="xsmall" color="status-critical">
                                            0
                                        </Text>
                                    </sup>
                                    <Text size="small">
                                        Congratulations to the team!
                                        <br />
                                        <Anchor href="https://www.twitch.tv/directory" target="_blank">
                                            Rate the match <FontAwesomeIcon icon={["fad", "thumbs-up"]} />
                                        </Anchor>
                                    </Text>
                                </Box>
                            </Box>
                        </Box>
                    </Tab> */}
                                    <Tab>
                                        <Box>
                                            <Text weight={"bold"} style={{ textTransform: "uppercase" }}>
                                                Team Members
                                            </Text>
                                            <Box direction="row" pad={"small"} wrap gap="small">
                                                {members.map((member) => (
                                                    <AliasCard key={member.id} width={150} alias={member.alias}>
                                                        <TeamMemberRole role={member.roleName}></TeamMemberRole>
                                                    </AliasCard>
                                                ))}
                                                {members.length == 0 && (
                                                    <Box align="center">
                                                        <Text textAlign="center">{team.name} has no team members</Text>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </Tab>

                                    <Tab>
                                        <Box gap="small">
                                            <Text weight={"bold"} style={{ textTransform: "uppercase" }}>
                                                Upcoming Matches
                                            </Text>
                                            {upcomingMatches.map((match) => (
                                                <MatchRow key={match.id} match={match}></MatchRow>
                                            ))}
                                            {upcomingMatches.length == 0 && (
                                                <Box align="center">
                                                    <Text textAlign="center">{team.name} has no upcoming matches</Text>
                                                </Box>
                                            )}
                                            <Text weight={"bold"} style={{ textTransform: "uppercase" }}>
                                                Latest Matches
                                            </Text>
                                            {pastMatches.map((match) => (
                                                <MatchRow key={match.id} match={match}></MatchRow>
                                            ))}
                                            {pastMatches.length == 0 && (
                                                <Box align="center">
                                                    <Text textAlign="center">{team.name} hasn't played any matches yet</Text>
                                                </Box>
                                            )}
                                        </Box>
                                    </Tab>

                                    <Tab>
                                        <Box gap="small">
                                            {tournaments.map((tournament) => (
                                                <TournamentRow key={tournament.id} tournament={tournament}>
                                                    <></>
                                                </TournamentRow>
                                            ))}
                                            {tournaments.length == 0 && (
                                                <Box align="center">
                                                    <Text textAlign="center">{team.name} hasn't played any tournaments yet</Text>
                                                </Box>
                                            )}
                                        </Box>
                                    </Tab>
                                    <Tab>
                                        <Box gap="small">
                                            {leagues.map((league) => (
                                                <Box>
                                                    <LeagueRow key={league.id} league={league}>
                                                        <></>
                                                    </LeagueRow>
                                                    <Box pad="small" gap="small">
                                                        {league.seasons?.map((season) => (
                                                            <SeasonRow key={season.id} season={season}>
                                                                <></>
                                                            </SeasonRow>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            ))}
                                            {leagues.length == 0 && (
                                                <Box align="center">
                                                    <Text textAlign="center">{team.name} hasn't played any leagues yet</Text>
                                                </Box>
                                            )}
                                        </Box>
                                    </Tab>
                                </Tabs>
                            </>
                        )}
                    </ThemeContext.Extend>
                </SkeletonWrapper>
            </Box>
        </Box>
    );
};

export default UXTeamHome;
